import { ApiError, api } from "@/lib/api";

export type MaterialsFolder = {
  id: number;
  account_id: number;
  brand_id: number | null;
  brand_site_id: number | null;
  parent_id: number | null;
  binding_kind: "normal" | "brand_root" | "brand_site_root";
  name: string;
  created_at?: string;
  updated_at?: string;
};

export type MaterialsListContext = {
  context: "enterprise" | "brand" | "brand_site";
  brandId?: string | number | null;
  brandSiteId?: string | number | null;
};

export type MaterialsListOptions = Partial<MaterialsListContext> & {
  scope?: string;
  folderId?: number | null;
  /** 为 true 时返回当前 context 整棵可见子树内的全部文件（面板搜索用） */
  subtree?: boolean;
};

export type MaterialsItemUsage = {
  brand_site_id: number;
  /** 站点标题 */
  title: string;
  context: string;
  /** 站点 brand_id，用于拼后台编辑路径 */
  brand_id?: number;
  slug?: string;
  public_url?: string | null;
  page_slug?: string | null;
  page_title?: string | null;
  frontend_url?: string | null;
  resolved?: boolean;
  asset_id?: number;
};

export type MaterialsItem = {
  id: number;
  account_id: number;
  brand_id: number | null;
  folder_id: number | null;
  name: string;
  /** 对外 Hashid，供 Hermes / 品牌站引用 */
  slug: string;
  notes: string | null;
  content_type: string | null;
  byte_size: number;
  uploaded_by_user_id: number;
  uploader_name: string;
  created_at: string;
  updated_at: string;
  source_kind?: "file" | "external_video" | string;
  external_provider?: string | null;
  external_video_id?: string | null;
  external_page_url?: string | null;
  external_embed_url?: string | null;
  /** 仅 show / 删前校验时有值 */
  usages?: MaterialsItemUsage[];
};

export type MaterialsBulkDestroyBlocked = {
  id: number;
  name: string;
  usages: MaterialsItemUsage[];
};

export type MaterialsBulkDestroyResult = {
  deleted: Array<{ id: number; name: string; slug: string }>;
  blocked: MaterialsBulkDestroyBlocked[];
  failed: Array<{ id: number; code: string }>;
};

export type MaterialsAuditEvent = {
  id: number;
  action: string;
  actor_user_id: number | null;
  actor_name: string | null;
  target_type: string | null;
  target_id: number | null;
  payload: Record<string, unknown>;
  created_at: string;
};

/** 带账户头的二进制下载（绕开 JSON parse） */
async function fetchBlobForAccount(
  accountId: string,
  path: string,
  retried = false,
): Promise<Response> {
  const headers = new Headers();
  const accessToken = api.getAccessToken();
  if (accessToken) headers.set("Authorization", `Bearer ${accessToken}`);
  headers.set("X-Account-Id", String(accountId));

  const url = path.startsWith("/api/") ? path : `/api/v1${path}`;
  const response = await window.fetch(url, { headers });

  if (response.status === 401 && !retried) {
    try {
      // 触发 api 层 refresh（与普通 JSON 请求同一路径）
      await api.fetch("/auth/me");
      return fetchBlobForAccount(accountId, path, true);
    } catch {
      // 继续抛出下方错误
    }
  }

  return response;
}

/** 全局上传队列用：带进度的 multipart，不依赖 createMaterialsApi 闭包。 */
export function uploadMaterialsFileWithProgress(
  accountId: string,
  form: FormData,
  opts: { onProgress?: (ratio: number) => void } = {},
): Promise<{ item: MaterialsItem }> {
  const url = "/api/v1/materials/items";

  const send = (retried: boolean) =>
    new Promise<{ item: MaterialsItem }>((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open("POST", url);
      const accessToken = api.getAccessToken();
      if (accessToken) xhr.setRequestHeader("Authorization", `Bearer ${accessToken}`);
      xhr.setRequestHeader("X-Account-Id", String(accountId));

      xhr.upload.onprogress = (event) => {
        if (!event.lengthComputable || !opts.onProgress) return;
        opts.onProgress(Math.min(1, event.loaded / event.total));
      };

      xhr.onload = () => {
        void (async () => {
          let payload: unknown = null;
          try {
            payload = xhr.responseText ? JSON.parse(xhr.responseText) : null;
          } catch {
            payload = null;
          }

          if (xhr.status === 401 && !retried) {
            try {
              await api.refreshAccessToken();
              resolve(await send(true));
              return;
            } catch {
              reject(new ApiError(401, payload));
              return;
            }
          }

          if (xhr.status < 200 || xhr.status >= 300) {
            reject(new ApiError(xhr.status, payload));
            return;
          }

          resolve(payload as { item: MaterialsItem });
        })();
      };

      xhr.onerror = () => reject(new ApiError(0, { error: { message: "网络错误，上传中断" } }));
      xhr.onabort = () => reject(new ApiError(0, { error: { message: "上传已取消" } }));
      xhr.send(form);
    });

  return send(false);
}

export function createMaterialsApi(accountId: string) {
  const prefix = "/materials";

  function contextQuery(opts: Partial<MaterialsListContext>): URLSearchParams {
    const query = new URLSearchParams();
    if (opts.context) query.set("context", opts.context);
    if (opts.brandId != null) query.set("brand_id", String(opts.brandId));
    if (opts.brandSiteId != null) query.set("brand_site_id", String(opts.brandSiteId));
    return query;
  }

  return {
    listFolders(opts: MaterialsListContext & { lean?: boolean }) {
      const query = contextQuery(opts);
      if (opts.lean) query.set("lean", "1");
      return api.fetchForAccount<{
        folders: MaterialsFolder[];
        work_root_id: number | null;
      }>(
        accountId,
        `${prefix}/folders?${query}`,
      );
    },

    listItems(opts: MaterialsListOptions) {
      const q = contextQuery(opts);
      if (opts.scope) q.set("scope", opts.scope);
      if (opts.folderId != null) q.set("folder_id", String(opts.folderId));
      if (opts.subtree) q.set("subtree", "1");
      return api.fetchForAccount<{ items: MaterialsItem[] }>(
        accountId,
        `${prefix}/items?${q}`,
      );
    },

    /** 单条素材详情（含 usages）；不存在时 404 */
    getItem(id: number) {
      return api.fetchForAccount<{ item: MaterialsItem }>(
        accountId,
        `${prefix}/items/${id}`,
      );
    },

    /** 经理 + 未锁账户：改文件名 / 备注 */
    patchItem(id: number, payload: { name?: string; notes?: string | null }) {
      return api.fetchForAccount<{ item: MaterialsItem }>(
        accountId,
        `${prefix}/items/${id}`,
        {
          method: "PATCH",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        },
      );
    },

    /** 经理 + 未锁账户；有品牌站引用时 422 in_use */
    deleteItem(id: number) {
      return api.fetchForAccount<{ item: { id: number; name: string; slug: string } }>(
        accountId,
        `${prefix}/items/${id}`,
        { method: "DELETE" },
      );
    },

    /** 批量删除素材；部分成功时返回 deleted / blocked / failed 明细 */
    bulkDeleteItems(ids: number[]) {
      return api.fetchForAccount<MaterialsBulkDestroyResult>(
        accountId,
        `${prefix}/items/bulk_destroy`,
        {
          method: "DELETE",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ ids }),
        },
      );
    },

    /** 批量移动文件与文件夹到目标目录（null = 全部素材根） */
    moveResources(payload: {
      itemIds: number[];
      folderIds: number[];
      targetFolderId: number | null;
    }) {
      return api.fetchForAccount<{
        moved: {
          items: { id: number; folder_id: number | null; brand_id: number | null }[];
          folders: { id: number; parent_id: number | null; brand_id: number | null }[];
        };
      }>(accountId, `${prefix}/move`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          item_ids: payload.itemIds,
          folder_ids: payload.folderIds,
          target_folder_id: payload.targetFolderId,
        }),
      });
    },

    patchFolder(id: number, payload: { name: string }) {
      return api.fetchForAccount<{ folder: MaterialsFolder }>(
        accountId,
        `${prefix}/folders/${id}`,
        {
          method: "PATCH",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        },
      );
    },

    deleteFolder(id: number) {
      return api.fetchForAccount<{ folder: { id: number; name: string } }>(
        accountId,
        `${prefix}/folders/${id}`,
        { method: "DELETE" },
      );
    },

    bulkDeleteFolders(ids: number[]) {
      return api.fetchForAccount<import("@/lib/materials-folder-ui").MaterialsBulkDestroyFoldersResult>(
        accountId,
        `${prefix}/folders/bulk_destroy`,
        {
          method: "DELETE",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ ids }),
        },
      );
    },

    createFolder(payload: {
      name: string;
      parent_id?: number | null;
      brand_id?: number | null;
    }) {
      return api.fetchForAccount<{ folder: MaterialsFolder }>(
        accountId,
        `${prefix}/folders`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        },
      );
    },

    createMarkdown(payload: {
      name: string;
      body: string;
      folder_id?: number | null;
      brand_id?: number | null;
    }) {
      return api.fetchForAccount<{ item: MaterialsItem }>(accountId, `${prefix}/items`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
    },

    createExternalVideo(payload: {
      external_video_input: string;
      name?: string;
      folder_id?: number | null;
      brand_id?: number | null;
    }) {
      return api.fetchForAccount<{ item: MaterialsItem }>(accountId, `${prefix}/items`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
    },

    /** FormData 须含 file；可选 name / folder_id / brand_id。勿手动设 Content-Type。 */
    uploadFile(form: FormData) {
      return api.fetchForAccount<{ item: MaterialsItem }>(accountId, `${prefix}/items`, {
        method: "POST",
        body: form,
      });
    },

    /**
     * 带上传进度的 multipart POST（XHR）。
     * onProgress 为 0–1；401 时尝试 refresh 后重试一次。
     */
    uploadFileWithProgress(
      form: FormData,
      opts: { onProgress?: (ratio: number) => void } = {},
    ): Promise<{ item: MaterialsItem }> {
      return uploadMaterialsFileWithProgress(accountId, form, opts);
    },

    listAuditEvents() {
      return api.fetchForAccount<{ audit_events: MaterialsAuditEvent[] }>(
        accountId,
        `${prefix}/audit_events`,
      );
    },

    async downloadItem(id: number, filename: string) {
      const response = await fetchBlobForAccount(
        accountId,
        `${prefix}/items/${id}/download`,
      );
      if (!response.ok) {
        let payload: unknown = null;
        try {
          payload = await response.json();
        } catch {
          payload = null;
        }
        throw new ApiError(response.status, payload);
      }
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement("a");
      anchor.href = url;
      anchor.download = filename;
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      URL.revokeObjectURL(url);
    },

    /** 预览用：取 blob，不记下载审计 */
    async fetchItemContent(id: number): Promise<Blob> {
      const response = await fetchBlobForAccount(
        accountId,
        `${prefix}/items/${id}/content`,
      );
      if (!response.ok) {
        let payload: unknown = null;
        try {
          payload = await response.json();
        } catch {
          payload = null;
        }
        throw new ApiError(response.status, payload);
      }
      return response.blob();
    },

    /** 列表/聊天缩略图：图片为 WebP 变体，不记下载审计 */
    async fetchItemPreview(id: number): Promise<Blob> {
      const response = await fetchBlobForAccount(
        accountId,
        `${prefix}/items/${id}/preview`,
      );
      if (!response.ok) {
        let payload: unknown = null;
        try {
          payload = await response.json();
        } catch {
          payload = null;
        }
        throw new ApiError(response.status, payload);
      }
      return response.blob();
    },
  };
}
